import React from "react";
import { Form, FormikProvider, useFormik } from "formik";
import { emptyToNull } from "@common/helpers/string";
import {
    CreateCounterpartyAddressBody,
    CounterpartyAddress,
} from "@common/schemas/counterpartyAddress.schema";
import { AddressForm } from "@components/forms/AddressForm";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { HStack } from "@design-system/HStack/HStack";
import { VStack } from "@design-system/VStack/VStack";
import { submitHelper } from "@helpers/form";
import { DEFAULT_COUNTRY } from "@components/counterparty/counterpartyFormSchema";
import {
    StandardModalBody,
    StandardModalFooter,
} from "@design-system/Modal/Modal";
import styles from "./fieldSpacing.module.scss";

interface AddressFormValues {
    label: string;
    street: string;
    street2: string;
    city: string;
    postalCode: string;
    state: string | null;
    country: string | null;
}

const fields = createFormikFields<AddressFormValues>();

function toFormValues(address?: CounterpartyAddress): AddressFormValues {
    return {
        label: address?.label ?? "",
        street: address?.street ?? "",
        street2: address?.street2 ?? "",
        city: address?.city ?? "",
        postalCode: address?.postalCode ?? "",
        state: address?.state ?? null,
        country: address?.country ?? DEFAULT_COUNTRY,
    };
}

function toAddressBody(
    values: AddressFormValues,
): CreateCounterpartyAddressBody {
    let { country, state } = values;
    if (country !== "US") {
        state = null;
    }
    const isAddressEmpty =
        !state &&
        !values.city &&
        !values.postalCode &&
        !values.street &&
        !values.street2;
    if (country === DEFAULT_COUNTRY && isAddressEmpty) {
        country = null;
    }
    return {
        label: emptyToNull(values.label),
        street: emptyToNull(values.street),
        street2: emptyToNull(values.street2),
        city: emptyToNull(values.city),
        postalCode: emptyToNull(values.postalCode),
        state: state ? emptyToNull(state) : null,
        country,
    };
}

interface Props {
    address?: CounterpartyAddress;
    formId?: string;
    submitText?: string;
    onSubmit: (body: CreateCounterpartyAddressBody) => Promise<void>;
    onCancelled: () => void;
}

export const CounterpartyAddressForm: React.FC<Props> = ({
    address,
    formId,
    submitText = "Save",
    onSubmit,
    onCancelled,
}) => {
    const formik = useFormik<AddressFormValues>({
        initialValues: toFormValues(address),
        onSubmit: submitHelper({
            handler: (values) => onSubmit(toAddressBody(values)),
        }),
    });

    return (
        <FormikProvider value={formik}>
            <Form id={formId}>
                <StandardModalBody>
                    <VStack>
                        <fields.Text
                            name="label"
                            label="Label"
                            placeholder="HQ, warehouse, billing…"
                            size="lg"
                            className={styles.inputField}
                        />
                        <AddressForm
                            includeCountry
                            includeStreet2
                            prefix=""
                            fieldSize="xs"
                        />
                    </VStack>
                </StandardModalBody>
                <StandardModalFooter>
                    <HStack gap={8} justify="end">
                        <Button
                            type="button"
                            variant="secondary"
                            size="md"
                            onClick={onCancelled}
                            disabled={formik.isSubmitting}
                        >
                            Cancel
                        </Button>
                        <ButtonWithLoader
                            type="submit"
                            variant="primary"
                            size="md"
                            loading={formik.isSubmitting}
                        >
                            {submitText}
                        </ButtonWithLoader>
                    </HStack>
                </StandardModalFooter>
            </Form>
        </FormikProvider>
    );
};
